/**
 * Training Upload Dialog - for users allowed to create training materials
 * Supports both Create and Edit modes
 * Upload videos, PDFs, docs, images for training materials
 */
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, Loader2, Save } from "lucide-react";
import {
  uploadTrainingAttachment,
  getMaterialType,
  createTrainingMaterial,
  updateTrainingMaterial,
  TrainingCategory,
  TrainingStatus,
  TrainingMaterial,
} from "@/services/trainingService";
import { useAuth } from "@/contexts/AuthContext";
import { useFileUpload } from "@/hooks/useFileUpload";
import { FileUploadSection } from "@/components/common/FileUploadSection";
import { useAppConfig } from "@/hooks/useAppConfig";

interface TrainingUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  editMaterial?: TrainingMaterial | null;
}

export function TrainingUploadDialog({
  open,
  onOpenChange,
  onSuccess,
  editMaterial,
}: TrainingUploadDialogProps) {
  const { user } = useAuth();
  const { config } = useAppConfig();
  const [submitting, setSubmitting] = useState(false);
  const isEditMode = !!editMaterial;

  const fileUpload = useFileUpload({
    maxSizeMB: config.trainingMaxFileUploadSizeMB,
    maxFiles: config.trainingMaxFileUploads,
    allowedFileTypes: config.trainingAllowedFileTypes,
    uploadMode: "deferred",
  });

  // Human-readable label derived from the live config
  const uploadHelpText = `${config.trainingAllowedFileTypes
    .map((t) => t.replace("/*", "").replace(".", "").toUpperCase())
    .join(", ")} (Max ${config.trainingMaxFileUploadSizeMB} MB)`;

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "general" as TrainingCategory,
    status: "new" as TrainingStatus,
    duration: "",
  });

  // Pre-fill form when editing; reset when dialog closes
  useEffect(() => {
    if (editMaterial && open) {
      setFormData({
        title: editMaterial.title,
        description: editMaterial.description || "",
        category: editMaterial.category,
        status: editMaterial.status,
        duration: editMaterial.duration || "",
      });
    } else if (!open) {
      setFormData({
        title: "",
        description: "",
        category: "general",
        status: "new",
        duration: "",
      });
      fileUpload.resetAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMaterial, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    if (!isEditMode && fileUpload.pendingFiles.length === 0) {
      toast.error("Please select a file to upload");
      return;
    }

    if (!formData.title.trim()) {
      toast.error("Please enter a title");
      return;
    }

    try {
      setSubmitting(true);

      if (isEditMode) {
        // ---- EDIT MODE ----
        const updates: Record<string, unknown> = {
          title: formData.title,
          description: formData.description,
          category: formData.category,
          status: formData.status,
        };

        if (formData.duration?.trim()) {
          updates.duration = formData.duration.trim();
        }

        // If a new file was staged, upload and replace
        if (fileUpload.pendingFiles.length > 0) {
          toast.info("Uploading new file…");
          const [attachment] = await fileUpload.executeUploads((file) =>
            uploadTrainingAttachment(file, user.phone),
          );
          updates.fileUrl = attachment.url;
          updates.fileName = attachment.name;
          updates.fileSize = attachment.size;
          updates.type = getMaterialType(attachment.name, attachment.type);
        }

        await updateTrainingMaterial(editMaterial!.id, updates);
        toast.success("Training material updated successfully!");
      } else {
        // ---- CREATE MODE ----
        toast.info("Uploading file…");
        const [attachment] = await fileUpload.executeUploads((file) =>
          uploadTrainingAttachment(file, user.phone),
        );

        const materialData: Record<string, unknown> = {
          title: formData.title,
          description: formData.description,
          type: getMaterialType(attachment.name, attachment.type),
          category: formData.category,
          status: formData.status,
          fileUrl: attachment.url,
          fileName: attachment.name,
          fileSize: attachment.size,
          uploadedBy: user.phone,
          uploadedByName: user.name,
        };

        if (formData.duration?.trim()) {
          materialData.duration = formData.duration.trim();
        }

        await createTrainingMaterial(
          materialData as Omit<
            TrainingMaterial,
            "id" | "viewCount" | "createdAt" | "updatedAt"
          >,
        );
        toast.success("Training material uploaded successfully!");
      }

      onSuccess();
      onOpenChange(false);
    } catch (error: unknown) {
      console.error("Upload error:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      toast.error(`Failed: ${errorMessage}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? "Edit Training Material" : "Upload Training Material"}
          </DialogTitle>
          <DialogDescription>
            {isEditMode
              ? "Update the training material details. You can optionally replace the file."
              : "Upload a new training material. Supports video, PDF, documents, and images."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* File Upload */}
          <div className="space-y-2">
            <Label>
              File {isEditMode ? "(optional — upload to replace)" : "*"}
            </Label>
            <FileUploadSection
              files={fileUpload.pendingFiles}
              onCameraCapture={fileUpload.handleCameraCapture}
              onFileUpload={fileUpload.handleFileUpload}
              onRemoveFile={fileUpload.handleRemovePendingFile}
              onImageClick={fileUpload.openImageViewer}
              imageViewerState={{
                ...fileUpload.imageViewerState,
                onClose: fileUpload.closeImageViewer,
              }}
              label="Training File"
              helpText={uploadHelpText}
              disabled={submitting}
            />
            {isEditMode &&
              editMaterial &&
              fileUpload.pendingFiles.length === 0 &&
              (editMaterial.type === "image" ? (
                <div className="mt-2 rounded-lg overflow-hidden border bg-muted">
                  <img
                    src={editMaterial.fileUrl}
                    alt={editMaterial.fileName}
                    className="w-full max-h-48 object-contain"
                  />
                  <p className="text-xs text-muted-foreground px-2 py-1">
                    Current: {editMaterial.fileName}
                  </p>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Current file: {editMaterial.fileName}
                </p>
              ))}
          </div>

          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, title: e.target.value }))
              }
              placeholder="e.g., Home Loan Processing Guidelines"
              disabled={submitting}
              required
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              placeholder="Brief description of the training material..."
              rows={3}
              disabled={submitting}
            />
          </div>

          {/* Category and Status */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Select
                value={formData.category}
                onValueChange={(value) =>
                  setFormData((prev) => ({
                    ...prev,
                    category: value as TrainingCategory,
                  }))
                }
                disabled={submitting}
              >
                <SelectTrigger id="category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="onboarding">Onboarding</SelectItem>
                  <SelectItem value="product">Product Knowledge</SelectItem>
                  <SelectItem value="sales">Sales Techniques</SelectItem>
                  <SelectItem value="compliance">Compliance</SelectItem>
                  <SelectItem value="technical">Technical</SelectItem>
                  <SelectItem value="general">General</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status Badge</Label>
              <Select
                value={formData.status}
                onValueChange={(value) =>
                  setFormData((prev) => ({
                    ...prev,
                    status: value as TrainingStatus,
                  }))
                }
                disabled={submitting}
              >
                <SelectTrigger id="status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="popular">Popular</SelectItem>
                  <SelectItem value="essential">Essential</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Duration */}
          <div className="space-y-2">
            <Label htmlFor="duration">Duration (optional)</Label>
            <Input
              id="duration"
              value={formData.duration}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, duration: e.target.value }))
              }
              placeholder="e.g., 15 min, 20 pages"
              disabled={submitting}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                submitting ||
                (!isEditMode && fileUpload.pendingFiles.length === 0) ||
                !formData.title
              }
              className="flex-1"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {isEditMode ? "Saving..." : "Uploading..."}
                </>
              ) : isEditMode ? (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
